import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ExportQuery, ExportUIModel } from '@y42/data-export';
import {
  BaseDataLineageNode,
  DataLineageDiagramHydrationService,
  DataLineageDiagramUIModel,
  DataLineageHydratationNode,
  DataLineageHydratationNodeMap,
  DataLineageNodeBinding,
  HydratedBaseDataLineageNode,
} from '@y42/data-lineage/diagram';
import { Y42FeatureTypeToChildLogo, Y42FeatureTypeToLogo } from '@y42/feature';
import { IntegrationQuery, IntegrationUIModel } from '@y42/integration';
import { JobStatus, Y42FeatureType } from '@y42/json-schema';
import { SqlModelingQuery, SqlModelingUIModel } from '@y42/sql-model';
import { UiModelingQuery, UiModelingUIModel } from '@y42/ui-model';
import { isNotNullish } from '@y42/util/nullish';
import { VisualizationDashboardQuery, VisualizationDashboardUIModel } from '@y42/visualization/dashboard';
import { VisualizationWidgetQuery, VisualizationWidgetUIModel } from '@y42/visualization/widget';
import isEqual from 'lodash/isEqual';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { generateUnsavedLineage } from '../utils/generate-unsaved-lineage';
import { getMultipleAssetDependencies } from '../utils/get-asset-dependencies';
import { getRefLineage } from '../utils/get-ref-lineage';

@UntilDestroy()
@Injectable()
export class Y42DataLineageDiagramHydrationService extends DataLineageDiagramHydrationService {
  constructor(
    private readonly integrationQuery: IntegrationQuery,
    private readonly uiModelingQuery: UiModelingQuery,
    private readonly sqlModelingQuery: SqlModelingQuery,
    private readonly exportQuery: ExportQuery,
    private readonly visualizationQuery: VisualizationDashboardQuery,
    private readonly visualizationWidgetQuery: VisualizationWidgetQuery,
  ) {
    super();
  }

  private readonly allItems$ = combineLatest([
    this.integrationQuery.loading$,
    this.uiModelingQuery.loading$,
    this.sqlModelingQuery.loading$,
    this.visualizationQuery.loading$,
    this.visualizationWidgetQuery.loading$,
    this.exportQuery.loading$,
  ]).pipe(
    map((loadings) => loadings.every((l) => !l)),
    filter((allLoaded) => !!allLoaded),
    switchMap(() =>
      combineLatest([
        this.integrationQuery.items$,
        this.uiModelingQuery.items$,
        this.sqlModelingQuery.items$,
        this.visualizationQuery.items$,
        this.visualizationWidgetQuery.items$,
        this.exportQuery.items$,
      ]),
    ),
    debounceTime(500),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
  );

  private readonly unsavedLineage$: Observable<DataLineageDiagramUIModel> = this.allItems$.pipe(
    switchMap(([integrations, uiModels, sqlModels, visualizations, widgets, exports]) =>
      generateUnsavedLineage(integrations, uiModels, sqlModels, visualizations, widgets, exports),
    ),
  );

  private readonly hydrationNodeMap$: Observable<DataLineageHydratationNodeMap> = this.allItems$.pipe(
    tap(([integrations, uiModels, sqlModels, visualizations, widgets, exports]) =>
      this.setTags(integrations, uiModels, sqlModels, visualizations, widgets, exports),
    ),
    map(([integrations, uiModels, sqlModels, visualizations, widgets, exports]) =>
      this.createHydratationNodeMap(integrations, uiModels, sqlModels, visualizations, widgets, exports),
    ),
  );

  hydrateGitNode<T extends BaseDataLineageNode>(node: T): Observable<HydratedBaseDataLineageNode<T>> {
    return this.hydrationNodeMap$.pipe(
      map((hydrationNodeMap) => {
        const hydrationNode = hydrationNodeMap[node.key] || null;

        return {
          ...node,
          [DataLineageNodeBinding.$Name]: node.key.split('/').at(-1) || null,
          [DataLineageNodeBinding.Name]: hydrationNode?.name || '',
          [DataLineageNodeBinding.Logo]: hydrationNode?.logo || null,
          [DataLineageNodeBinding.Status]: hydrationNode?.status || null,
          [DataLineageNodeBinding.AssetStatus]: hydrationNode?.assetStatus || null,
          [DataLineageNodeBinding.ItemType]: hydrationNode?.type || null,
          [DataLineageNodeBinding.Tags]: hydrationNode?.tags || [],
          [DataLineageNodeBinding.DeletedEntity]: !hydrationNode,
          [DataLineageNodeBinding.HasReadPermission]: !!hydrationNode?.hasReadPermission,
          [DataLineageNodeBinding.ValidColumnsCount]: hydrationNode?.validColumnsCount,
        };
      }),
    );
  }

  hydrateGitNodes<T extends BaseDataLineageNode>(nodes: T[]): Observable<Array<HydratedBaseDataLineageNode<T>>> {
    return nodes.length > 0 ? combineLatest(nodes.map((node) => this.hydrateGitNode(node))) : of([]);
  }

  getRefLineage(assetPath: string) {
    return this.unsavedLineage$.pipe(map((tempLineage) => getRefLineage(assetPath, tempLineage)));
  }

  getAssetDependencies(assetPaths: string[]) {
    return this.unsavedLineage$.pipe(map((tempLineage) => getMultipleAssetDependencies(assetPaths, tempLineage)));
  }

  private createHydratationNodeMap(
    integrations: IntegrationUIModel[],
    uiModels: UiModelingUIModel[],
    sqlModels: SqlModelingUIModel[],
    dashboards: VisualizationDashboardUIModel[],
    widgets: VisualizationWidgetUIModel[],
    exports: ExportUIModel[],
  ): DataLineageHydratationNodeMap {
    const hydratationNodeMap = {} as DataLineageHydratationNodeMap;

    integrations.forEach((integration) => {
      hydratationNodeMap[integration.$path] = {
        [DataLineageNodeBinding.Name]: integration.name,
        [DataLineageNodeBinding.Logo]: integration?.typeModel?.logo || null,
        [DataLineageNodeBinding.Status]: this.getParentStatus(integration.tables.map((t) => t.latest_job?.status)),
        [DataLineageNodeBinding.AssetStatus]: integration.asset_status,
        [DataLineageNodeBinding.Tags]: integration.tags,
        [DataLineageNodeBinding.HasReadPermission]: integration.hasReadPermission,
        type: integration.typeModel?.type || null,
        tableCount: integration.tables.length,
      } as DataLineageHydratationNode;

      integration.tables.forEach((table) => {
        hydratationNodeMap[table.$path] = {
          [DataLineageNodeBinding.Name]: table.name,
          [DataLineageNodeBinding.Logo]: Y42FeatureTypeToChildLogo[Y42FeatureType.Integration],
          [DataLineageNodeBinding.Status]: table.latest_job?.status || null,
          [DataLineageNodeBinding.AssetStatus]: table.asset_status,
          [DataLineageNodeBinding.Tags]: table.tags,
          [DataLineageNodeBinding.HasReadPermission]: integration.hasReadPermission,
          [DataLineageNodeBinding.ValidColumnsCount]: table.latest_job?.columns.length,
        } as DataLineageHydratationNode;
      });
    });

    const modelHydratationFn = (model: UiModelingUIModel | SqlModelingUIModel) => {
      hydratationNodeMap[model.$path] = {
        [DataLineageNodeBinding.Name]: model.name,
        [DataLineageNodeBinding.Logo]: model?.typeModel?.logo || null,
        [DataLineageNodeBinding.Status]: this.getParentStatus(model.tables.map((t) => t.latest_job?.status)),
        [DataLineageNodeBinding.AssetStatus]: model.asset_status,
        [DataLineageNodeBinding.Tags]: model.tags,
        [DataLineageNodeBinding.HasReadPermission]: model.hasReadPermission,
        type: model?.typeModel?.type || null,
        tableCount: model.tables.length,
      } as DataLineageHydratationNode;

      model.tables.forEach((table) => {
        hydratationNodeMap[table.$path] = {
          [DataLineageNodeBinding.Name]: table.name,
          [DataLineageNodeBinding.Logo]: Y42FeatureTypeToChildLogo[Y42FeatureType.UiModel],
          [DataLineageNodeBinding.Status]: table.latest_job?.status || null,
          [DataLineageNodeBinding.AssetStatus]: table.asset_status,
          [DataLineageNodeBinding.Tags]: table.tags,
          [DataLineageNodeBinding.HasReadPermission]: model.hasReadPermission,
          [DataLineageNodeBinding.ValidColumnsCount]: table.latest_job?.columns.length,
        } as DataLineageHydratationNode;
      });
    };

    uiModels.forEach(modelHydratationFn);
    sqlModels.forEach(modelHydratationFn);

    dashboards.forEach((dashboard) => {
      hydratationNodeMap[dashboard.$path] = {
        [DataLineageNodeBinding.Name]: dashboard.name,
        [DataLineageNodeBinding.Logo]: Y42FeatureTypeToLogo[Y42FeatureType.VisualizationDashboard],
        [DataLineageNodeBinding.AssetStatus]: dashboard.asset_status,
        [DataLineageNodeBinding.Tags]: dashboard.tags,
        [DataLineageNodeBinding.HasReadPermission]: dashboard.hasReadPermission,
      } as DataLineageHydratationNode;

      dashboard.tabs.forEach((tab) => {
        hydratationNodeMap[tab.$path] = {
          [DataLineageNodeBinding.Name]: tab.name,
          [DataLineageNodeBinding.Logo]: Y42FeatureTypeToChildLogo[Y42FeatureType.VisualizationDashboard],
          [DataLineageNodeBinding.AssetStatus]: tab.asset_status,
          [DataLineageNodeBinding.Tags]: tab.tags,
          [DataLineageNodeBinding.HasReadPermission]: dashboard.hasReadPermission,
        } as DataLineageHydratationNode;
      });
    });

    widgets.forEach((widget) => {
      hydratationNodeMap[widget.$path] = {
        [DataLineageNodeBinding.Name]: widget.name,
        [DataLineageNodeBinding.Logo]: Y42FeatureTypeToLogo[Y42FeatureType.VisualizationWidget],
        [DataLineageNodeBinding.AssetStatus]: widget.asset_status,
        [DataLineageNodeBinding.Tags]: widget.tags,
        [DataLineageNodeBinding.HasReadPermission]: widget.hasReadPermission,
      } as DataLineageHydratationNode;
    });

    exports.forEach((exportItem) => {
      hydratationNodeMap[exportItem.$path] = {
        [DataLineageNodeBinding.Name]: exportItem.name,
        [DataLineageNodeBinding.Logo]: exportItem.typeModel.logo,
        [DataLineageNodeBinding.Status]: this.getParentStatus(exportItem.triggers.map((t) => t.latest_job?.status)),
        [DataLineageNodeBinding.AssetStatus]: exportItem.asset_status,
        [DataLineageNodeBinding.Tags]: exportItem.tags,
        [DataLineageNodeBinding.HasReadPermission]: exportItem.hasReadPermission,
        type: exportItem.typeModel.type,
        tableCount: exportItem.triggers.length,
      } as DataLineageHydratationNode;

      exportItem.triggers.forEach((trigger) => {
        hydratationNodeMap[trigger.$path] = {
          [DataLineageNodeBinding.Name]: trigger.name,
          [DataLineageNodeBinding.Logo]: Y42FeatureTypeToChildLogo[Y42FeatureType.Export],
          [DataLineageNodeBinding.Status]: trigger.latest_job?.status || null,
          [DataLineageNodeBinding.AssetStatus]: trigger.asset_status,
          [DataLineageNodeBinding.Tags]: trigger.tags,
          [DataLineageNodeBinding.HasReadPermission]: exportItem.hasReadPermission,
        } as DataLineageHydratationNode;
      });
    });

    return hydratationNodeMap;
  }

  private getParentStatus(childStatuses: Array<JobStatus | undefined>) {
    if (childStatuses.every((s) => s === JobStatus.Ready)) {
      return JobStatus.Ready;
    }

    if (childStatuses.some((s) => s === JobStatus.Invalid)) {
      return JobStatus.Invalid;
    }

    return null;
  }

  private setTags(
    integrations: IntegrationUIModel[],
    uiModels: UiModelingUIModel[],
    sqlModels: SqlModelingUIModel[],
    dashboards: VisualizationDashboardUIModel[],
    widgets: VisualizationWidgetUIModel[],
    exports: ExportUIModel[],
  ) {
    const tags: string[][] = [];

    const tagsExtractionHelper = <T extends { tags: string[] }>(collection: T[]) => {
      collection.forEach((item) => {
        tags.push(item.tags);
      });
    };

    const datasources = [...integrations, ...uiModels, ...sqlModels];

    tagsExtractionHelper(datasources);
    tagsExtractionHelper(datasources.map((d) => d.tables).flat());

    tagsExtractionHelper(dashboards);
    tagsExtractionHelper(dashboards.map((d) => d.tabs).flat());

    tagsExtractionHelper(widgets);

    tagsExtractionHelper(exports);
    tagsExtractionHelper(exports.map((d) => d.triggers).flat());

    const allTags = [...new Set(tags.flat())].filter((t) => isNotNullish(t));

    this.allTags$$.next(allTags);
  }
}
